import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';

import { CalendarEventDto, CalendarEventType } from '@/types/calendar';
import { toLocalIso } from '@/utils/datetime';
import {
  createClientCalendarEvent,
  updateCalendarEvent,
} from '@services/calendar.service';

import { FormPayload, FeelingForm } from './event-forms/FeelingForm';
import { FluidForm } from './event-forms/FluidForm';
import { TaskForm } from './event-forms/TaskForm';
import { BaseDialog } from '@components/common/BaseDialog';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';

// Їжа/тренування/препарати мають власні модалки (словник, нутрієнти/калорії) - сюди не потрапляють.
export type IntakeEventCode = 'food' | 'workout' | 'medication';
const INTAKE_CODES: string[] = ['food', 'workout', 'medication'];

interface AddClientEventModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  eventTypes: CalendarEventType[];
  defaultDate?: Date;
  eventToEdit?: CalendarEventDto | null;
  onSuccess: () => void;
  onIntakeTypeSelected: (code: IntakeEventCode) => void;
  defaultEventTypeId?: number | null;
}

const FORM_BY_CODE: Record<string, React.FC<any>> = {
  feeling: FeelingForm,
  fluid: FluidForm,
  task: TaskForm,
};

export const AddClientEventModal: React.FC<AddClientEventModalProps> = ({
  open,
  onClose,
  clientId,
  eventTypes,
  defaultDate,
  eventToEdit,
  onSuccess,
  onIntakeTypeSelected,
  defaultEventTypeId,
}) => {
  const isEditing = !!eventToEdit;

  const [eventTypeId, setEventTypeId] = useState<number | ''>('');
  const [scheduledAt, setScheduledAt] = useState<Date | null>(defaultDate ?? new Date());
  const [formPayload, setFormPayload] = useState<FormPayload>({ title: '', isValid: false });
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (eventToEdit) {
        setEventTypeId(eventToEdit.eventType.id);
        setScheduledAt(eventToEdit.scheduledLdt ? new Date(eventToEdit.scheduledLdt) : new Date());
      } else {
        setEventTypeId(defaultEventTypeId ?? eventTypes.find(et => !INTAKE_CODES.includes(et.code))?.id ?? '');
        setScheduledAt(defaultDate ?? new Date());
      }
      setFormPayload({ title: '', isValid: false });
      setIsCompleted(eventToEdit?.isCompleted ?? false);
      setError(null);
    }
  }, [open, eventToEdit, defaultDate, eventTypes, defaultEventTypeId]);

  const selectedEventType = eventTypes.find((et) => et.id === eventTypeId);
  const eventTypeCode = selectedEventType?.code ?? '';
  const FormComponent = FORM_BY_CODE[eventTypeCode] ?? null;

  useEffect(() => {
    // Батько закриває цю модалку сам і зберігає дату клітинки для модалки їжі/тренування/препарату.
    if (INTAKE_CODES.includes(eventTypeCode) && !isEditing && open) {
      onIntakeTypeSelected(eventTypeCode as IntakeEventCode);
    }
  }, [eventTypeCode, isEditing, open, onIntakeTypeSelected]);

  const handleFormChange = useCallback((payload: FormPayload) => {
    setFormPayload(payload);
  }, []);

  const handleSave = async () => {
    if (!formPayload.isValid || !scheduledAt || !eventTypeId) return;

    setLoading(true);
    setError(null);

    try {
      if (isEditing && eventToEdit) {
        await updateCalendarEvent(eventToEdit.id, {
          title: formPayload.title,
          scheduledLdt: toLocalIso(scheduledAt),
          details: formPayload.details,
          isCompleted: isCompleted,
        });
      } else {
        // Без calendarId бекенд кладе подію в календар експерта; виконання при створенні - через completedAt.
        const scheduledLdt = toLocalIso(scheduledAt);
        await createClientCalendarEvent(clientId, {
          eventTypeId: eventTypeId as number,
          title: formPayload.title,
          scheduledLdt,
          details: formPayload.details,
          completedAt: isCompleted ? scheduledLdt : null,
        });
      }
      onSuccess();

      onClose();
    } catch (err: any) {
      setError(err?.message || (isEditing ? 'Не вдалося оновити подію' : 'Не вдалося створити подію'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={isEditing ? 'Редагувати подію' : 'Додати подію клієнту'}
      iDoc={ModalIndices.ADD_CLIENT_EVENT}
      actions={
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !formPayload.isValid || !scheduledAt}
        >
          {loading ? <CircularProgress size={24} /> : isEditing ? 'Зберегти' : 'Додати'}
        </Button>
      }
    >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth margin="dense" variant="outlined" disabled={loading || isEditing}>
          <InputLabel>Тип події</InputLabel>
          <Select
            value={eventTypeId}
            onChange={(e) => {
              setEventTypeId(e.target.value as number);
              setFormPayload({ title: '', isValid: false });
            }}
            label="Тип події"
          >
            {eventTypes.map((et) => (
              <MenuItem key={et.id} value={et.id}>
                {et.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {FormComponent && (
          <FormComponent
            key={eventTypeCode}
            disabled={loading}
            eventToEdit={eventToEdit}
            onChange={handleFormChange}
          />
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
          <DateTimePicker
            label="Запланований час"
            value={scheduledAt}
            onChange={(newValue) => setScheduledAt(newValue)}
            disabled={loading}
            slotProps={{ textField: { fullWidth: true, margin: 'dense' } }}
          />
        </LocalizationProvider>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCompleted}
                onChange={(e) => setIsCompleted(e.target.checked)}
                disabled={loading}
              />
            }
            label="Позначити як виконане"
          />
        </Box>
    </BaseDialog>
  );
};
